import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getPillar, PILLARS } from "@/data/pillars";
import PageHead from "@/components/PageHead";
import { useGetStarted } from "@/components/GetStartedDialog";
import { ArrowRight, ChevronRight } from "lucide-react";

const BRAND_URL = "https://redkeyweb.design";

export default function ServicePillar() {
  const { slug } = useParams();
  const pillar = getPillar(slug);
  const { open } = useGetStarted();
  const [work, setWork] = useState([]);

  useEffect(() => {
    if (!pillar?.relatedWorkIds?.length) return;
    let mounted = true;
    api.get("/portfolio").then((r) => {
      if (!mounted) return;
      const map = new Map(r.data.map((x) => [x.id, x]));
      setWork(pillar.relatedWorkIds.map((id) => map.get(id)).filter(Boolean));
    }).catch(() => {});
    return () => { mounted = false; };
  }, [pillar]);

  if (!pillar) return <Navigate to="/services" replace />;

  const Icon = pillar.Icon;
  const canonical = `${BRAND_URL}/services/${pillar.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BRAND_URL}/` },
          { "@type": "ListItem", position: 2, name: "Services", item: `${BRAND_URL}/services` },
          { "@type": "ListItem", position: 3, name: pillar.name, item: canonical },
        ],
      },
      {
        "@type": "Service",
        name: pillar.name,
        provider: { "@type": "Organization", name: "Redkey Web Design", url: BRAND_URL },
        serviceType: pillar.keywords,
        areaServed: { "@type": "Country", name: "United States" },
        description: pillar.metaDescription,
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: pillar.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div data-testid={`pillar-page-${pillar.slug}`}>
      <PageHead
        title={pillar.metaTitle}
        description={pillar.metaDescription}
        canonical={canonical}
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav className="mx-auto max-w-[1360px] px-6 md:px-10 pt-10" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 font-ui text-[12px] tracking-widest uppercase text-label">
          <li><Link to="/" className="link-underline-grow">Home</Link></li>
          <li aria-hidden><ChevronRight size={12} /></li>
          <li><Link to="/services" className="link-underline-grow">Services</Link></li>
          <li aria-hidden><ChevronRight size={12} /></li>
          <li className="text-ink">{pillar.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-10 md:pt-14 pb-16">
        <div className="flex items-center gap-4">
          <span className="font-ui text-sm text-label tracking-widest">{pillar.kicker}</span>
          <span className="h-px w-10 bg-[hsl(var(--line-strong))]" />
          <Icon size={22} strokeWidth={1.75} className="text-ink" aria-hidden />
          <span className="eyebrow">Service Pillar</span>
        </div>
        <h1 className="font-display text-5xl md:text-7xl mt-6 max-w-[22ch]" data-testid={`pillar-h1-${pillar.slug}`}>
          {pillar.name}
        </h1>
        <p className="mt-6 font-display text-2xl md:text-3xl text-brand-red-ink leading-snug max-w-[36ch]">
          {pillar.heroLine}
        </p>
        <p className="mt-6 max-w-prose text-lg text-ink-soft leading-relaxed">
          {pillar.intro}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={open}
            data-testid={`pillar-hero-cta-${pillar.slug}`}
            className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
          <Link
            to="/services"
            className="inline-flex items-center gap-3 border hairline-strong px-6 py-4 hover:border-ink transition-colors duration-200 font-ui"
          >
            All services <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Approach */}
      <section className="border-t hairline bg-paper-alt">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow">§ Approach</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">{pillar.approachLead}</h2>
          </div>
          <ol className="md:col-span-8 space-y-0">
            {pillar.approach.map((a, i) => (
              <li key={a.heading} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-6 border-t hairline first:border-t-0 items-start">
                <div className="md:col-span-2 font-ui text-sm text-label tracking-widest">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="md:col-span-4 font-display text-xl md:text-2xl">{a.heading}</h3>
                <p className="md:col-span-6 font-body text-ink-soft leading-relaxed">{a.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What's included */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow">§ Scope</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">What&rsquo;s included</h2>
          </div>
          <ul className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {pillar.included.map((item) => (
              <li key={item} className="flex items-start gap-3 font-body text-ink text-lg">
                <span className="text-brand-red mt-2 inline-block h-1.5 w-1.5 rounded-full shrink-0" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </div>
        {/* SR-only topical keyword list for crawlers */}
        <p className="sr-only">
          {pillar.name} topics: {pillar.keywords.join(", ")}.
        </p>
      </section>

      {/* Related work */}
      {work.length > 0 && (
        <section className="border-t hairline bg-paper-alt" data-testid={`pillar-work-${pillar.slug}`}>
          <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <p className="eyebrow">§ Proof</p>
                <h2 className="font-display text-3xl md:text-4xl mt-4">Related work</h2>
              </div>
              <Link to="/work" className="font-ui link-underline">See all work →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {work.map((w) => (
                <Link
                  key={w.id}
                  to={`/work/${w.id}`}
                  data-testid={`pillar-work-card-${pillar.slug}-${w.id}`}
                  className="portfolio-card group block"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-paper border hairline">
                    <img
                      src={w.image}
                      alt={`${w.title} — case study cover`}
                      loading="lazy"
                      decoding="async"
                      className="portfolio-img w-full h-full object-cover"
                    />
                  </div>
                  <p className="eyebrow mt-3">{w.category}</p>
                  <h3 className="font-display text-xl mt-1 group-hover:text-electric transition-colors">
                    {w.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="border-t hairline" data-testid={`pillar-faq-${pillar.slug}`}>
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow">§ FAQ</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">Common questions.</h2>
            <p className="mt-4 text-ink-soft max-w-prose">
              Have a different question?{" "}
              <button type="button" onClick={open} className="text-electric-ink link-underline">
                Ask directly
              </button>.
            </p>
          </div>
          <dl className="md:col-span-8 divide-y divide-[hsl(var(--line-strong))] border-t border-b border-[hsl(var(--line-strong))]">
            {pillar.faq.map((f, i) => (
              <div key={f.q} className="py-6" data-testid={`pillar-faq-item-${pillar.slug}-${i}`}>
                <dt className="font-display text-xl md:text-2xl text-ink">{f.q}</dt>
                <dd className="mt-3 font-body text-ink-soft leading-relaxed max-w-prose">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Cross-pillar navigation */}
      <section className="border-t hairline bg-ink text-paper">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24">
          <p className="font-ui text-[11px] tracking-widest text-paper/60 mb-6">§ Other Services</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.filter((p) => p.slug !== pillar.slug).map((p) => {
              const PIcon = p.Icon;
              return (
                <Link
                  key={p.slug}
                  to={`/services/${p.slug}`}
                  data-testid={`pillar-cross-${pillar.slug}-to-${p.slug}`}
                  className="border border-paper/25 p-6 hover:border-brand-red transition-colors group block"
                >
                  <PIcon size={18} strokeWidth={1.75} className="text-paper/70 group-hover:text-brand-red transition-colors" aria-hidden />
                  <p className="eyebrow text-paper/60 mt-3">{p.kicker} · Service</p>
                  <h3 className="font-display text-2xl mt-2 text-paper group-hover:text-brand-red transition-colors">
                    {p.name}
                  </h3>
                  <p className="mt-3 font-body text-paper/75 text-sm leading-relaxed">
                    {p.hubTagline}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-14 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-display text-3xl md:text-4xl max-w-[26ch]">
            Ready to talk through a {pillar.name.toLowerCase()} project?
          </p>
          <button
            type="button"
            onClick={open}
            data-testid={`pillar-bottom-cta-${pillar.slug}`}
            className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
        </div>
      </section>
    </div>
  );
}
